import { store } from "index";
import {
	getDefaultStorageSettings,
	getDefaultReducer,
	filterByLoadTime,
	loadFromStorage,
	listenToLocalStorageChange,
} from "m-model-common";
import { getJoiObjectKeys, validateStorage } from "m-model-joi";
import { createModel, RawInstances, createCRUDActionTypes } from "m-model-core";
import { MAX_LOAD_TIME_DIFF, MIN_LOAD_TIME } from "./constants";
import { QueryMetaInfo } from "./utils/query-meta";
import {
	ISchoolStructure,
	SchoolStructureSchema,
	ISchoolLabel,
} from "@app/api/schools/structure/helper-schemas";
import { ObjectId } from "@app/utils/generics";
import { isNonNullable } from "@app/utils/common";

const keyOfId = "_id";
type IdKey = typeof keyOfId;
type DOC = ISchoolStructure;
export type IStateSchoolStructures = RawInstances<IdKey, DOC>;

// ==============Base Model=================

const dockeys = getJoiObjectKeys<DOC>(SchoolStructureSchema);
const storage = localStorage;
const actionTypes = createCRUDActionTypes("schoolStructure");
const storageSettings = getDefaultStorageSettings("schoolStructures");
const metaInformationName = "schoolStructuresMetaInformation";

const isLoadedRecentlyEnough = filterByLoadTime(
	MAX_LOAD_TIME_DIFF,
	MIN_LOAD_TIME
);

const Model = createModel<IdKey, DOC>({
	keyOfId,
	getInstances: (() => store.getState().schoolStructures) as any,
	dispatch: (action => store.dispatch(action)) as any,
	subscribe: (listener => store.subscribe(listener)) as any,
	actionTypes,
	dockeys,
	loadInstancesFromStorage: () =>
		loadFromStorage({
			storage,
			key: storageSettings.itemName,
			validateWholeData: validateStorage(
				"ObjectId",
				SchoolStructureSchema
			),
			filter: isLoadedRecentlyEnough,
		}),
});

// ==============Main Model=================

export class SchoolStructure extends Model {
	static initialize() {
		const info = super.initialize();
		if (info.loadedAll) this.meta.initialize();
		else this.meta.clear();
		return info;
	}
	static findBySchool(schoolId: number) {
		return this.findOneSync({ schoolId });
	}

	static meta = new QueryMetaInfo(storage, metaInformationName);

	getRootLabels() {
		return this.labels.filter(e => !e.parentLabelIds);
	}

	getLeafLabels() {
		return this.labels.filter(e => this.getChildLabels(e._id).length === 0);
	}

	getDescendantLabels(labelId: ObjectId): ISchoolLabel[] {
		const children = this.getChildLabels(labelId);
		const allDescendants = [...children];
		children.forEach(child =>
			allDescendants.push(...this.getDescendantLabels(child._id))
		);
		return allDescendants;
	}

	getParentsOfLabel(labelId: ObjectId): ISchoolLabel[] {
		const label = this.getLabelById(labelId);
		if (!label?.parentLabelIds) return [];
		return label.parentLabelIds
			.map(id => this.getLabelById(id))
			.filter(isNonNullable);
	}

	getAncestorLabels(labelId: ObjectId): ISchoolLabel[] {
		const parents = this.getParentsOfLabel(labelId);
		const allAcnestorLabels = [...parents];
		parents.forEach(parent =>
			allAcnestorLabels.push(...this.getParentsOfLabel(parent._id))
		);
		return allAcnestorLabels;
	}

	getChildLabels(labelId: ObjectId) {
		return this.labels.filter(
			e => e.parentLabelIds && e.parentLabelIds.includes(labelId)
		);
	}

	getLabelById(labelId: ObjectId) {
		return this.labels.find(e => e._id === labelId);
	}

	getRootOfLabel(labelId: ObjectId): ISchoolLabel | undefined {
		const label = this.getLabelById(labelId);
		if (!label?.parentLabelIds) return label;
		return this.getRootOfLabel(label.parentLabelIds[0]);
	}

	getParentOfLabel(labelId: ObjectId): ISchoolLabel | undefined {
		const label = this.getLabelById(labelId);
		if (!label?.parentLabelIds) return label;
		return this.getLabelById(label.parentLabelIds[0]);
	}
}

// ==============ETC=================

listenToLocalStorageChange(storage, metaInformationName, SchoolStructure.meta);

export const schoolStructuresReducer = getDefaultReducer(
	storageSettings,
	() => SchoolStructure
);
